/* ==========================================================================
   <device-search> — Decalz device finder
   Builds one searchable index of every device the store makes skins for,
   from two source shapes that coexist in the catalog:
     mode "products"    → one product per device  (phones, laptops, MacBooks)
     mode "collections" → one collection per device (cameras, consoles, buds)
   Products are fetched from /collections/<handle>/products.json (public Ajax
   API, 250/page) and cached in sessionStorage. Collections are rendered into
   the config by Liquid, so they need no fetch.
   ========================================================================== */
(function () {
  'use strict';

  var CACHE_PREFIX = 'ds:v3:';
  var CACHE_TTL = 24 * 60 * 60 * 1000; // 24h
  var RECENT_KEY = 'ds:recent';
  /* 100 per page, not the 250 the Ajax API allows: this catalog reliably 500s
     on some 250-item pages, and 100 comes back clean every time. */
  var PAGE_SIZE = 100;
  var CHUNK = 24;

  /* ---------------------------------------------------------------- utils */

  function norm(s) {
    return (s || '')
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  }

  function squash(s) {
    return norm(s).replace(/ /g, '');
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  /* Levenshtein with an early bail once the distance exceeds max. */
  function editDistance(a, b, max) {
    var al = a.length;
    var bl = b.length;
    if (Math.abs(al - bl) > max) return max + 1;
    var prev = new Array(bl + 1);
    var cur = new Array(bl + 1);
    var i, j;
    for (j = 0; j <= bl; j++) prev[j] = j;
    for (i = 1; i <= al; i++) {
      cur[0] = i;
      var best = cur[0];
      for (j = 1; j <= bl; j++) {
        var cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        cur[j] = Math.min(cur[j - 1] + 1, prev[j] + 1, prev[j - 1] + cost);
        if (cur[j] < best) best = cur[j];
      }
      if (best > max) return max + 1;
      var swap = prev;
      prev = cur;
      cur = swap;
    }
    return prev[bl];
  }

  /* Returns the character span the needle occupies inside hay, or 0 when the
     needle is not a subsequence. A short span means the letters sit together,
     which is what makes an abbreviation feel intentional rather than accidental. */
  function subsequenceSpan(needle, hay) {
    var i = 0;
    var start = -1;
    for (var j = 0; j < hay.length && i < needle.length; j++) {
      if (hay.charCodeAt(j) === needle.charCodeAt(i)) {
        if (start === -1) start = j;
        i++;
        if (i === needle.length) return j - start + 1;
      }
    }
    return 0;
  }

  function cacheGet(key) {
    try {
      var raw = localStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.t > CACHE_TTL) return null;
      return parsed.d;
    } catch (e) {
      return null;
    }
  }

  function cacheSet(key, data) {
    try {
      localStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ t: Date.now(), d: data }));
    } catch (e) {
      /* quota or private mode — the index still works, it just refetches.
         Clear old versions so a full store never wedges the cache. */
      try {
        for (var i = localStorage.length - 1; i >= 0; i--) {
          var k = localStorage.key(i);
          if (k && k.indexOf('ds:v') === 0 && k.indexOf(CACHE_PREFIX) !== 0) localStorage.removeItem(k);
        }
      } catch (e2) { /* nothing else to try */ }
    }
  }

  function thumbUrl(src, width) {
    if (!src) return '';
    if (/[?&]width=/.test(src)) return src;
    return src + (src.indexOf('?') > -1 ? '&' : '?') + 'width=' + width;
  }


  /* Device-kind glyphs. The catalog's product images are all *design* art — the
     same graffiti or marble on every model — so a thumbnail grid would say
     nothing about the device. A line silhouette per category does. */
  var GLYPHS = {
    phones: '<rect x="7" y="2" width="10" height="20" rx="2"/><path d="M11 18.5h2"/>',
    laptops: '<rect x="5" y="5" width="14" height="10" rx="1"/><path d="M2.5 18.5h19"/>',
    macbooks: '<rect x="5" y="5" width="14" height="10" rx="1"/><path d="M2.5 18.5h19"/>',
    tablets: '<rect x="4.5" y="2.5" width="15" height="19" rx="2"/><path d="M11 18.5h2"/>',
    watches: '<rect x="7" y="6.5" width="10" height="11" rx="2.5"/><path d="M9.5 6.5V3h5v3.5M9.5 17.5V21h5v-3.5"/>',
    cameras: '<rect x="2.5" y="7" width="19" height="13" rx="2"/><circle cx="12" cy="13.5" r="4"/><path d="M8 7l1.4-2.6h5.2L16 7"/>',
    lenses: '<circle cx="12" cy="12" r="8.5"/><circle cx="12" cy="12" r="3.5"/>',
    consoles: '<rect x="2" y="7.5" width="20" height="9" rx="4.5"/><path d="M6.5 12h3.4M8.2 10.3v3.4"/><circle cx="16.4" cy="11.2" r="1"/><circle cx="18.4" cy="13.2" r="1"/>',
    earbuds: '<circle cx="7.5" cy="8.5" r="3"/><path d="M7.5 11.5v7"/><circle cx="16.5" cy="8.5" r="3"/><path d="M16.5 11.5v7"/>',
    drones: '<circle cx="5" cy="6" r="2.6"/><circle cx="19" cy="6" r="2.6"/><rect x="9" y="10" width="6" height="6" rx="1.4"/><path d="M6.8 7.9 9.6 10.6M17.2 7.9 14.4 10.6"/>',
    'car-keys': '<circle cx="8" cy="12" r="4"/><path d="M12 12h9M17.5 12v3M20.5 12v3.8"/>',
    cards: '<rect x="2.5" y="5" width="19" height="14" rx="2"/><path d="M2.5 10h19"/>'
  };

  function glyph(slug) {
    var body = GLYPHS[slug] || '<circle cx="12" cy="12" r="8.5"/>';
    return (
      '<svg class="ds__glyph" viewBox="0 0 24 24" fill="none" stroke="currentColor" ' +
      'stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
      body +
      '</svg>'
    );
  }


  /* Series is what turns 575 phones into a list you can actually walk:
     "Galaxy S", "Galaxy A", "iPhone", "ROG", "ThinkPad". Derived from the name
     so a new model joins its series without anyone maintaining a list. */
  var SERIES_WORDS = [
    'z fold', 'z flip', 'galaxy tab', 'iphone', 'ipad', 'macbook air', 'macbook pro',
    'macbook', 'apple watch', 'airpods', 'rog phone', 'zenbook', 'vivobook', 'thinkpad',
    'ideapad', 'legion', 'yoga', 'victus', 'omen', 'pavilion', 'elitebook', 'probook',
    'envy', 'spectre', 'inspiron', 'latitude', 'vostro', 'alienware', 'xps', 'predator',
    'nitro', 'aspire', 'swift', 'nord', 'narzo', 'pixel', 'razr', 'edge', 'notebook', 'note',
    'reno', 'find', 'magic', 'nova'
  ];

  var SERIES_CASE = {
    iphone: 'iPhone', ipad: 'iPad', macbook: 'MacBook', 'macbook air': 'MacBook Air',
    'macbook pro': 'MacBook Pro', airpods: 'AirPods', 'apple watch': 'Apple Watch',
    'z fold': 'Z Fold', 'z flip': 'Z Flip', 'galaxy tab': 'Galaxy Tab', 'rog phone': 'ROG Phone',
    thinkpad: 'ThinkPad', ideapad: 'IdeaPad', vivobook: 'VivoBook', zenbook: 'ZenBook',
    elitebook: 'EliteBook', probook: 'ProBook', xps: 'XPS', notebook: 'Notebook'
  };

  function seriesOf(name, brand) {
    var n = norm(name);
    for (var i = 0; i < SERIES_WORDS.length; i++) {
      if (n.indexOf(SERIES_WORDS[i]) > -1) {
        var w = SERIES_WORDS[i];
        return SERIES_CASE[w] || w.replace(/(^|\s)\S/g, function (c) { return c.toUpperCase(); });
      }
    }
    // letter+digits model codes, with or without a variant tail:
    // S25 -> S series, A56 -> A series, V40e -> V series, Z9s -> Z series
    var words = n.split(' ');
    for (var j = 0; j < words.length; j++) {
      var w = words[j];
      if (w.length >= 2 && w.length <= 6 && /^[a-z]\d+[a-z]{0,2}$/.test(w)) {
        return w.charAt(0).toUpperCase() + ' series';
      }
    }
    return '';
  }


  /* When every model in view shares its family word ("iPhone"), that word is
     not a filter — it is the whole set. The useful axis there is the variant
     suffix, which is what the Apple collection page has always used:
     Standard / Pro / Pro Max / Plus / Mini / SE / Air. */
  var VARIANTS = [
    ['pro max', 'Pro Max'], ['pro', 'Pro'], ['ultra', 'Ultra'], ['max', 'Max'],
    ['plus', 'Plus'], ['mini', 'Mini'], ['air', 'Air'], ['se', 'SE'],
    ['fe', 'FE'], ['lite', 'Lite'], ['neo', 'Neo'], ['prime', 'Prime']
  ];

  function variantOf(name) {
    var n = ' ' + norm(name) + ' ';
    for (var i = 0; i < VARIANTS.length; i++) {
      if (n.indexOf(' ' + VARIANTS[i][0] + ' ') > -1) return VARIANTS[i][1];
    }
    // trailing "e" models: iPhone 16e, 17e
    if (/\d+e\b/.test(n)) return 'e';
    return 'Standard';
  }

  /* ------------------------------------------------------------- element */

  function DeviceSearch() {}

  DeviceSearch.prototype = {
    /* ---- setup ---------------------------------------------------- */

    boot: function () {
      var cfgEl = this.el.querySelector('[data-ds-config]');
      if (!cfgEl) return;

      try {
        this.cfg = JSON.parse(cfgEl.textContent);
      } catch (e) {
        return;
      }

      this.root = this.el.querySelector('.ds');
      this.input = this.el.querySelector('[data-ds-input]');
      this.clearBtn = this.el.querySelector('[data-ds-clear]');
      this.readout = this.el.querySelector('[data-ds-readout]');
      this.typeRow = this.el.querySelector('[data-ds-types]');
      this.brandWrap = this.el.querySelector('[data-ds-brands]');
      this.brandRow = this.el.querySelector('[data-ds-brand-row]');
      this.seriesWrap = this.el.querySelector('[data-ds-series]');
      this.seriesRow = this.el.querySelector('[data-ds-series-row]');
      this.brandMore = this.el.querySelector('[data-ds-brand-more]');
      this.results = this.el.querySelector('[data-ds-results]');
      this.statusText = this.el.querySelector('[data-ds-status]');
      this.resetBtn = this.el.querySelector('[data-ds-reset]');
      this.empty = this.el.querySelector('[data-ds-empty]');
      this.emptyClear = this.el.querySelector('[data-ds-empty-clear]');
      this.suggests = this.el.querySelector('[data-ds-suggests]');
      this.skeleton = this.el.querySelector('[data-ds-skeleton]');
      this.errorBox = this.el.querySelector('[data-ds-error]');
      this.recent = this.el.querySelector('[data-ds-recent]');
      this.recentRow = this.el.querySelector('[data-ds-recent-row]');
      this.live = this.el.querySelector('[data-ds-live]');
      this.rail = this.el.querySelector('[data-ds-rail]');
      this.titleEl = this.el.querySelector('[data-ds-title]');
      this.crumbTail = this.el.querySelector('[data-ds-crumb-tail]');
      this.baseTitle = this.titleEl ? this.titleEl.innerHTML : '';
      this.baseDocTitle = document.title;
      this.scrim = this.el.querySelector('[data-ds-scrim]');
      this.applyBtn = this.el.querySelector('[data-ds-apply]');
      this.pills = this.el.querySelector('[data-ds-pills]');
      this.sortBtns = this.el.querySelectorAll('[data-ds-sort]');
      this.typeWrap = this.el.querySelector('[data-ds-typegrp]');
      this.typeMore = this.el.querySelector('[data-ds-type-more]');
      this.filtersSort = this.el.querySelector('[data-ds-filters-sort]');
      this.facets = this.el.querySelector('[data-ds-facets]');
      this.filtersToggle = this.el.querySelector('[data-ds-filters-toggle]');
      this.filtersCount = this.el.querySelector('[data-ds-filters-count]');

      this.index = [];
      this.view = [];
      this.rendered = 0;
      this.query = '';
      this.types = [];
      this.brands = [];
      this.series = [];
      this.variants = [];
      this.sort = 'new';
      this.activeIdx = -1;
      this.seq = 0;

      this.brandPriority = {};
      (this.cfg.brandPriority || []).forEach(function (b, i) {
        this.brandPriority[b] = i;
      }, this);

      /* ?canvas=dark|ink|light lets a preview link override the section
         setting, so alternatives can be compared without a theme edit. */
      var canvasOverride = new URLSearchParams(window.location.search).get('canvas');
      if (canvasOverride && /^(dark|ink|light)$/.test(canvasOverride)) {
        this.root.classList.remove('ds--ink', 'ds--light');
        if (canvasOverride !== 'dark') this.root.classList.add('ds--' + canvasOverride);
      }

      this.readState();
      /* A brand that arrived in the URL came from the menu and is settled, so
         its controls can go. A brand the visitor picked here must stay
         changeable — hiding the rail then would trap them. */
      this.urlPinned = this.brands.length === 1;
      this.bind();
      this.sortBtns.forEach(function (b) {
        b.setAttribute('aria-pressed', b.getAttribute('data-ds-sort') === this.sort ? 'true' : 'false');
      }, this);
      this.trackHeader();
      this.load();
    },

    /* The theme header is sticky and hides on scroll, so following its animated
       position every frame made the search bar chase it — that was the jitter.
       The offset is now measured only on load and resize, and CSS position:
       sticky does the rest with no per-frame JavaScript at all. */
    trackHeader: function () {
      var self = this;
      var header = document.querySelector('header.shopify-section-group-header-group') ||
        document.querySelector('[id*="__header"].shopify-section');

      function measure() {
        var top = 0;
        if (header) {
          var cs = getComputedStyle(header);
          if (cs.position === 'sticky' || cs.position === 'fixed') {
            top = Math.round(header.getBoundingClientRect().height);
          }
        }
        self.root.style.setProperty('--ds-sticky-top', top + 'px');
      }

      measure();
      var t;
      window.addEventListener('resize', function () {
        clearTimeout(t);
        t = setTimeout(measure, 150);
      }, { passive: true });

      // a shadow once the field parts company with the page, via one observer
      var sentinel = this.el.querySelector('[data-ds-stick-sentinel]');
      if (sentinel && 'IntersectionObserver' in window) {
        new IntersectionObserver(function (entries) {
          self.root.classList.toggle('is-stuck', !entries[0].isIntersecting);
        }, { threshold: 0, rootMargin: '-80px 0px 0px 0px' }).observe(sentinel);
      }
    },

    bind: function () {
      var self = this;

      this.input.addEventListener('input', function () {
        clearTimeout(self.debounce);
        self.debounce = setTimeout(function () {
          self.query = self.input.value.trim();
          self.activeIdx = -1;
          self.apply();
          self.writeState();
        }, 110);
      });

      this.input.addEventListener('keydown', function (e) {
        if (e.key === 'Escape') {
          if (self.input.value) {
            self.setQuery('');
          } else {
            self.input.blur();
          }
        } else if (e.key === 'ArrowDown') {
          e.preventDefault();
          self.move(1, true);
        } else if (e.key === 'Enter') {
          var first = self.el.querySelector('.ds__tile');
          if (first && self.view.length) {
            e.preventDefault();
            first.click();
          }
        }
      });

      this.clearBtn.addEventListener('click', function () {
        self.setQuery('');
        self.input.focus();
      });

      /* On a phone the rail is a bottom sheet, so it opens on demand. */
      function setSheet(open) {
        self.root.classList.toggle('is-filters-open', open);
        if (self.filtersToggle) self.filtersToggle.setAttribute('aria-expanded', open ? 'true' : 'false');
        if (self.rail) self.rail.setAttribute('aria-hidden', open ? 'false' : 'true');
      }
      this.setSheet = setSheet;

      if (this.filtersToggle) {
        this.filtersToggle.addEventListener('click', function () {
          setSheet(!self.root.classList.contains('is-filters-open'));
        });
      }

      if (this.applyBtn) {
        this.applyBtn.addEventListener('click', function () { setSheet(false); });
      }

      if (this.scrim) {
        this.scrim.addEventListener('click', function () { setSheet(false); });
      }

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.root.classList.contains('is-filters-open')) setSheet(false);
      });

      this.el.querySelectorAll('[data-ds-grp-toggle]').forEach(function (btn) {
        btn.addEventListener('click', function () {
          var grp = btn.closest('[data-ds-grp]');
          var open = grp.getAttribute('data-open') !== 'false';
          grp.setAttribute('data-open', open ? 'false' : 'true');
          var mark = btn.querySelector('span');
          if (mark) mark.textContent = open ? '+' : '−';
        });
      });

      this.sortBtns.forEach(function (btn) {
        btn.addEventListener('click', function () {
          self.sort = btn.getAttribute('data-ds-sort');
          self.sortBtns.forEach(function (b) {
            b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
          });
          self.apply();
          self.writeState();
        });
      });

      if (this.emptyClear) {
        this.emptyClear.addEventListener('click', function () {
          self.types = [];
          self.brands = [];
          self.series = [];
          self.apply();
          self.writeState();
          self.buildChips();
          self.syncChips();
        });
      }

      this.resetBtn.addEventListener('click', function () {
        self.types = [];
        self.brands = [];
        self.series = [];
        self.variants = [];
        self.setQuery('');
        self.buildChips();
        self.syncChips();
        self.input.focus();
      });

      this.el.addEventListener('click', function (e) {
        var pill = e.target.closest('[data-ds-pill]');
        if (pill) {
          var v = pill.getAttribute('data-ds-pill');
          var list = self.pillKey === 'series' ? 'series' : 'variants';
          self[list] = v ? [v] : [];
          self.apply();
          self.writeState();
          self.buildPills();
          self.buildChips();
          self.syncChips();
          return;
        }

        var chip = e.target.closest('[data-ds-type], [data-ds-brand], [data-ds-series-value]');
        if (chip) {
          self.toggleChip(chip);
          return;
        }
        var tile = e.target.closest('[data-ds-handle]');
        if (tile) self.remember(tile.getAttribute('data-ds-handle'));
      });

      if (this.typeMore) {
        this.typeMore.addEventListener('click', function () {
          var expanded = self.typeWrap.getAttribute('data-expanded') === 'true';
          self.typeWrap.setAttribute('data-expanded', expanded ? 'false' : 'true');
          self.typeMore.textContent = expanded ? self.typeMoreLabel : 'Show fewer';
        });
      }

      if (this.brandMore) {
        this.brandMore.addEventListener('click', function () {
          var expanded = self.brandWrap.getAttribute('data-expanded') === 'true';
          self.brandWrap.setAttribute('data-expanded', expanded ? 'false' : 'true');
          self.brandMore.textContent = expanded ? self.moreLabel : 'Show fewer brands';
        });
      }

      this.results.addEventListener('keydown', function (e) {
        var cols = self.columns();
        if (e.key === 'ArrowRight') { e.preventDefault(); self.move(1); }
        else if (e.key === 'ArrowLeft') { e.preventDefault(); self.move(-1); }
        else if (e.key === 'ArrowDown') { e.preventDefault(); self.move(cols); }
        else if (e.key === 'ArrowUp') {
          e.preventDefault();
          if (self.activeIdx - cols < 0) { self.input.focus(); self.activeIdx = -1; self.paintActive(); }
          else self.move(-cols);
        } else if (e.key === 'Escape') {
          self.input.focus();
        }
      });

      document.addEventListener('keydown', function (e) {
        if (e.defaultPrevented) return;
        var tag = (e.target.tagName || '').toLowerCase();
        if (tag === 'input' || tag === 'textarea' || tag === 'select' || e.target.isContentEditable) return;
        if (e.key === '/' || ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k')) {
          e.preventDefault();
          self.input.focus();
          self.input.select();
        }
      });

      window.addEventListener('popstate', function () {
        self.readState();
        self.input.value = self.query;
        self.syncChips();
        self.apply();
      });

      this.sentinel = this.el.querySelector('[data-ds-sentinel]');
      if (this.sentinel && 'IntersectionObserver' in window) {
        this.io = new IntersectionObserver(function (entries) {
          if (entries[0].isIntersecting) self.renderMore();
        }, { rootMargin: '600px 0px' });
        this.io.observe(this.sentinel);
      }
    },

    /* ---- data ------------------------------------------------------ */

    load: function () {
      var self = this;
      var cats = this.cfg.categories || [];
      cats.forEach(function (cat, i) { cat.order = i; });

      // Collection-mode devices are already in the config — show them at once.
      cats.forEach(function (cat) {
        (cat.items || []).forEach(function (it) {
          self.add({ n: it.n, u: it.u, i: it.i, b: it.b, p: '', t: [] }, cat);
        });
      });

      var jobs = [];
      cats.forEach(function (cat) {
        (cat.sources || []).forEach(function (handle) {
          jobs.push({ handle: handle, cat: cat });
        });
      });

      var bySlug = {};
      cats.forEach(function (cat) { bySlug[cat.slug] = cat; });

      function liveFetch() {
        if (!jobs.length) { self.ready(); return; }

        /* Two passes. A pre-tagged arrival only needs its own collection to be
           usable; everything else streams in behind it. */
        var wanted = self.types.length ? self.types : [(cats[0] || {}).slug];
        var first = jobs.filter(function (j) { return wanted.indexOf(j.cat.slug) > -1; });
        var rest = jobs.filter(function (j) { return wanted.indexOf(j.cat.slug) === -1; });
        if (!first.length) { first = jobs.slice(0, 1); rest = jobs.slice(1); }

        if (self.brands.length === 1) {
          var map = self.cfg.brandSources || {};
          var handle = map[self.brands[0]];
          if (handle && first.length) {
            rest = first.concat(rest);
            first = [{ handle: handle, cat: first[0].cat }];
          }
        }

        function runPool(list, size) {
          var next = 0;
          function worker() {
            if (next >= list.length) return Promise.resolve();
            var job = list[next++];
            return self.fetchCollection(job.handle, job.cat).then(worker);
          }
          var lanes = [];
          for (var i = 0; i < Math.min(size, list.length); i++) lanes.push(worker());
          return Promise.all(lanes);
        }

        runPool(first, 3)
          .then(function () { self.ready(); self.backgroundPass(rest); })
          .catch(function () { self.ready(); self.showError(); });
      }

      /* The whole index ships as one static asset — 18 KB gzipped off the CDN,
         instead of ~20 live API calls whose latency we do not control. The live
         path stays as the fallback and as the top-up when the file goes stale. */
      if (!this.cfg.indexUrl) { liveFetch(); return; }

      fetch(this.cfg.indexUrl, { credentials: 'omit' })
        .then(function (r) {
          if (!r.ok) throw new Error(String(r.status));
          return r.json();
        })
        .then(function (data) {
          var items = (data && data.items) || [];
          if (!items.length) throw new Error('empty index');

          items.forEach(function (it) {
            var cat = bySlug[it.c];
            if (cat) self.add({ n: it.n, u: it.u, b: it.b, p: it.p, t: [] }, cat);
          });

          self.ready();

          var days = (Date.now() - Date.parse(data.built || 0)) / 864e5;
          if (!(days >= 0) || days > (self.cfg.indexStaleDays || 7)) {
            // file has aged out — top up from the live collections quietly
            self.backgroundPass(jobs);
          }
        })
        .catch(function () {
          liveFetch();
        });
    },

    /* The rest of the catalogue arrives after the page is already usable. */
    backgroundPass: function (rest) {
      var self = this;
      if (!rest || !rest.length) return;

      var before = this.query + '|' + this.types + '|' + this.brands + '|' + this.series;

      function go() {
        var next = 0;
        function worker() {
          if (next >= rest.length) return Promise.resolve();
          var job = rest[next++];
          return self.fetchCollection(job.handle, job.cat).then(worker);
        }
        var lanes = [];
        for (var i = 0; i < Math.min(2, rest.length); i++) lanes.push(worker());
        Promise.all(lanes).then(function () {
          self.buildChips();
          self.syncChips();
          var after = self.query + '|' + self.types + '|' + self.brands + '|' + self.series;
          // only redraw the results if the visitor has not moved on
          if (after === before) self.apply();
          if (self.partialLoad) self.showError();
        });
      }

      if (window.requestIdleCallback) requestIdleCallback(go, { timeout: 1200 });
      else setTimeout(go, 400);
    },

    fetchCollection: function (handle, cat) {
      var self = this;
      var cached = cacheGet(handle);
      if (cached) {
        cached.forEach(function (it) { self.add(it, cat); });
        return Promise.resolve();
      }

      var maxDevices = this.cfg.maxDevices || 1200;

      function fetchPage(limit, pageNo) {
        var url =
          '/collections/' + encodeURIComponent(handle) +
          '/products.json?limit=' + limit + '&page=' + pageNo;
        return fetch(url, { credentials: 'same-origin' }).then(function (r) {
          if (!r.ok) throw new Error(String(r.status));
          return r.json();
        });
      }

      function map(products) {
        return products.map(function (p) {
          var img = '';
          if (p.images && p.images.length) img = p.images[0].src;
          else if (p.featured_image) img = p.featured_image;
          var keep = {
            n: p.title,
            u: '/products/' + p.handle,
            b: p.vendor || '',
            p: p.published_at || p.created_at || ''
          };
          // tags are only needed for the exclude check, and images only when
          // thumbnails are on — carrying either into the cache doubles its size
          if (self.cfg.excludeTags && self.cfg.excludeTags.length) keep.t = p.tags || [];
          if (self.cfg.showThumbs) keep.i = img;
          return keep;
        });
      }

      /* Pages go out in waves rather than one-at-a-time. The phone collection
         is six pages; chained that was ~8s of round trips before the index was
         usable. A wave of four cuts it to about two. */
      var WAVE = 3;

      function run(limit) {
        var acc = [];
        var lastPage = Math.ceil(maxDevices / limit);

        function wave(from) {
          var calls = [];
          var to = Math.min(from + WAVE - 1, lastPage);
          for (var p = from; p <= to; p++) {
            calls.push(
              fetchPage(limit, p)
                .then(function (json) { return (json && json.products) || []; })
                .catch(function () { return null; })
            );
          }
          return Promise.all(calls).then(function (pages) {
            var full = false;
            for (var i = 0; i < pages.length; i++) {
              if (pages[i] === null) throw new Error('page failed');
              acc = acc.concat(map(pages[i]));
              full = pages[i].length === limit;
            }
            if (full && to < lastPage) return wave(to + 1);
            return acc;
          });
        }

        return fetchPage(limit, 1)
          .then(function (json) {
            var first = (json && json.products) || [];
            acc = acc.concat(map(first));
            if (first.length < limit || lastPage < 2) return acc;
            return wave(2);
          });
      }

      return run(PAGE_SIZE)
        .catch(function () { return run(50); })
        .then(function (items) {
          cacheSet(handle, items);
          items.forEach(function (it) { self.add(it, cat); });
          self.paintProgress();
        })
        .catch(function () {
          self.partialLoad = true;
        });
    },

    add: function (item, cat) {
      var url = item.u;
      if (!url || this.seen[url]) return;

      // designs that share a collection with real devices (tag:universal and
      // friends) are not devices — they would show up as models named "Flowers"
      var drop = this.cfg.excludeTags || [];
      var tags = item.t || [];
      for (var i = 0; i < drop.length; i++) {
        for (var j = 0; j < tags.length; j++) {
          if (String(tags[j]).toLowerCase() === drop[i]) return;
        }
      }

      if (cat.mode === 'products') {
        var need = this.cfg.requireWords || [];
        if (need.length) {
          var raw = String(item.n || '').toLowerCase();
          var ok = false;
          for (var k = 0; k < need.length; k++) {
            if (raw.indexOf(need[k]) > -1) { ok = true; break; }
          }
          // a device product is titled "<model> Skins & Wraps"; the design
          // products sharing its collection are titled just "Pink Marble"
          if (!ok) return;
        }
      }

      var skip = this.cfg.excludeWords || [];
      if (skip.length) {
        var lower = String(item.n || '').toLowerCase();
        for (var x = 0; x < skip.length; x++) {
          if (lower.indexOf(skip[x]) > -1) return;
        }
      }

      var name = this.clean(item.n, cat);
      if (!name) return;

      var key = cat.slug + '|' + norm(name);
      if (this.seenName[key]) return; // the catalog holds duplicate products
      this.seenName[key] = true;
      this.seen[url] = true;

      var brand = this.brandOf(name, item.b, cat);
      var n = norm(name);

      this.index.push({
        name: name,
        url: url,
        img: item.i ? thumbUrl(item.i, 300) : '',
        brand: brand,
        cat: cat.slug,
        catLabel: cat.label,
        catOrder: cat.order || 0,
        pub: item.p ? Date.parse(item.p) || 0 : 0,
        series: seriesOf(name, brand),
        variant: variantOf(name),
        n: n,
        s: n.replace(/ /g, ''),
        w: n.split(' '),
        a: n.split(' ').map(function (word) { return word.charAt(0); }).join('')
      });
    },

    clean: function (name, cat) {
      var out = String(name || '').replace(/\s+/g, ' ').trim();
      var strips = (cat.strip || []).concat(this.cfg.strip || []);
      strips.forEach(function (phrase) {
        if (!phrase) return;
        var lower = out.toLowerCase();
        var target = phrase.toLowerCase();
        var at = lower.indexOf(target);
        while (at > -1) {
          out = (out.slice(0, at) + ' ' + out.slice(at + target.length)).replace(/\s+/g, ' ').trim();
          lower = out.toLowerCase();
          at = lower.indexOf(target);
        }
      });
      // "(Only Back No Edge to Edge)", "(Only bottom half will come)" are
      // production notes, not part of the device's name
      out = out.replace(/\s*\([^)]*\bonly\b[^)]*\)/gi, '');
      out = out.replace(/\s{2,}/g, ' ');
      return out.replace(/[\s\-–—|,/]+$/, '').replace(/^[\s\-–—|,/]+/, '').trim();
    },

    brandOf: function (name, vendor, cat) {
      if (cat.brand) return cat.brand;
      var ignore = this.cfg.brandIgnore || [];
      var n = ' ' + norm(name) + ' ';
      var map = this.cfg.brands || [];
      for (var i = 0; i < map.length; i++) {
        if (ignore.indexOf(map[i].label.toLowerCase()) > -1) continue;
        var keys = map[i].k;
        for (var j = 0; j < keys.length; j++) {
          if (n.indexOf(' ' + keys[j] + ' ') > -1 || n.indexOf(' ' + keys[j]) === 0) return map[i].label;
        }
      }
      if (vendor && ignore.indexOf(vendor.toLowerCase()) === -1) return vendor;
      return 'Other';
    },

    /* Results appear collection by collection rather than after the slowest
       one, so the list is usable while the tail is still arriving. */
    paintProgress: function () {
      var self = this;
      if (this.loaded) return;
      clearTimeout(this.progressTimer);
      this.progressTimer = setTimeout(function () {
        if (self.loaded) return;
        self.skeleton.hidden = true;
        self.apply();
      }, 40);
    },

    ready: function () {
      this.skeleton.hidden = true;
      this.buildChips();
      this.syncChips();
      this.apply();
      this.paintRecent();
      this.loaded = true;
    },

    /* ---- filtering -------------------------------------------------- */

    score: function (item, q, qs, qWords) {
      if (item.n === q) return 1000;
      if (item.n.indexOf(q) === 0) return 940 - Math.min(item.n.length, 40);
      if (item.s.indexOf(qs) === 0) return 900;

      var i;
      for (i = 0; i < item.w.length; i++) {
        if (item.w[i].indexOf(q) === 0) return 860 - i * 4;
      }
      if (item.n.indexOf(q) > -1) return 780;
      if (item.s.indexOf(qs) > -1) return 740;

      // every query word must hit some word of the name (prefix)
      var all = true;
      for (i = 0; i < qWords.length; i++) {
        var hit = false;
        for (var j = 0; j < item.w.length; j++) {
          if (item.w[j].indexOf(qWords[i]) === 0) { hit = true; break; }
        }
        if (!hit) { all = false; break; }
      }
      if (all && qWords.length) return 700 - qWords.length;

      // initials: "ps5" finds "Play Station 5 Fat", "zf" finds "Z Fold"
      if (qs.length >= 2 && item.a.indexOf(qs) === 0) return 830;
      if (qs.length >= 3 && item.a.indexOf(qs) > -1) return 640;

      // subsequence, but only when the letters sit close together
      if (qs.length >= 3) {
        var span = subsequenceSpan(qs, item.s);
        var maxSpan = qs.length <= 4 ? qs.length + 2 : qs.length * 2;
        if (span > 0 && span <= maxSpan) return 430 - (span - qs.length) * 8;
      }

      // typo tolerance: one edit for short queries, two once there is room for it
      var max = qs.length <= 6 ? 1 : 2;
      var best = max + 1;
      for (i = 0; i < item.w.length; i++) {
        var word = item.w[i];
        // words under 4 characters are too easy to reach by accident
        if (word.length < 4) continue;
        if (Math.abs(word.length - q.length) > max + 2) continue;
        var d = editDistance(q, word, max);
        if (d < best) best = d;
        if (best === 0) break;
      }
      if (best <= max) return 400 - best * 60;

      var dWhole = editDistance(qs, item.s.slice(0, qs.length + max), max);
      if (dWhole <= max) return 340 - dWhole * 60;

      return 0;
    },

    apply: function () {
      var self = this;
      var q = norm(this.query);
      var qs = squash(this.query);
      var qWords = q ? q.split(' ') : [];
      var out = [];

      for (var i = 0; i < this.index.length; i++) {
        var it = this.index[i];
        if (this.types.length && this.types.indexOf(it.cat) === -1) continue;
        if (this.brands.length && this.brands.indexOf(it.brand) === -1) continue;
        if (this.series.length && this.series.indexOf(it.series) === -1) continue;
        if (this.variants.length && this.variants.indexOf(it.variant) === -1) continue;
        if (!q) {
          out.push(it);
          continue;
        }
        var sc = this.score(it, q, qs, qWords);
        if (sc > 0) {
          it._s = sc;
          out.push(it);
        }
      }

      if (q) {
        out.sort(function (a, b) {
          return b._s - a._s || a.name.length - b.name.length || a.name.localeCompare(b.name);
        });
      } else if (this.sort === 'az') {
        out.sort(function (a, b) {
          return a.catOrder - b.catOrder || a.name.localeCompare(b.name);
        });
      } else {
        var weight = this.catBrandWeight || {};
        var prio = this.brandPriority || {};
        out.sort(function (a, b) {
          return (
            a.catOrder - b.catOrder ||
            (prio[a.brand] === undefined ? 99 : prio[a.brand]) -
              (prio[b.brand] === undefined ? 99 : prio[b.brand]) ||
            (weight[b.cat + '|' + b.brand] || 0) - (weight[a.cat + '|' + a.brand] || 0) ||
            a.brand.localeCompare(b.brand) ||
            b.pub - a.pub ||
            a.name.localeCompare(b.name)
          );
        });
      }

      this.view = out;
      this.rendered = 0;
      this.activeIdx = -1;
      this.lastGroupRendered = null;
      this.currentGrid = null;
      this.results.innerHTML = '';
      this.root.classList.toggle('is-searching', !!this.query);

      var count = out.length;
      this.readout.innerHTML = '<b>' + count + '</b> ' + (count === 1 ? 'device' : 'devices');
      this.statusText.innerHTML = this.statusLine(count);
      this.resetBtn.hidden = !(this.query || this.types.length || this.brands.length || this.series.length || this.variants.length);
      if (this.applyBtn) {
        this.applyBtn.textContent = 'Show ' + count + ' device' + (count === 1 ? '' : 's');
      }
      if (this.filtersSort) {
        this.filtersSort.textContent = this.sort === 'az' ? 'A–Z' : 'New first';
      }
      if (this.recent) this.recent.hidden = !!(this.query || this.types.length || this.brands.length || this.series.length) || !this.recentCount;

      if (!count) {
        this.showEmpty(q, qs);
      } else {
        this.empty.hidden = true;
        this.renderMore();
      }

      this.retitle();
      this.buildPills();

      clearTimeout(this.liveTimer);
      this.liveTimer = setTimeout(function () {
        self.live.textContent = count + ' device' + (count === 1 ? '' : 's') + ' found';
      }, 500);
    },


    /* A menu click arrives as ?type=phones&brand=Vivo. That view is a brand
       page as far as the visitor is concerned, so the headline, breadcrumb and
       document title say "Vivo Phones" rather than leaving the generic H1 on. */
    scopeLabel: function () {
      var cats = this.cfg.categories || [];
      var typeLabels = this.types.map(function (t) {
        for (var i = 0; i < cats.length; i++) {
          if (cats[i].slug === t) return cats[i].label;
        }
        return t;
      });
      var parts = [];
      if (this.brands.length) parts.push(this.brands.join(' + '));
      if (this.series.length) parts.push(this.series.join(' + '));
      if (typeLabels.length) parts.push(typeLabels.join(' + '));
      return parts.join(' ');
    },

    retitle: function () {
      if (!this.titleEl) return;
      var label = this.query ? '' : this.scopeLabel();

      this.root.classList.toggle('is-scoped', !!label);

      if (label) {
        this.titleEl.textContent = label;
        if (this.crumbTail) this.crumbTail.textContent = label;
        document.title = label + ' — Decalz';
      } else {
        this.titleEl.innerHTML = this.baseTitle;
        if (this.crumbTail) this.crumbTail.textContent = this.crumbBase || this.crumbTail.textContent;
        document.title = this.baseDocTitle;
      }
    },

    statusLine: function (count) {
      var bits = [];
      if (this.query) bits.push('"' + this.query + '"');
      if (this.types.length) {
        var labels = [];
        (this.cfg.categories || []).forEach(function (c) {
          if (this.types.indexOf(c.slug) > -1) labels.push(c.label);
        }, this);
        bits.push(labels.join(' + '));
      }
      if (this.brands.length) bits.push(this.brands.join(' + '));
      if (this.series.length) bits.push(this.series.join(' + '));
      if (this.variants.length) bits.push(this.variants.join(' + '));
      var scope = bits.join(' · ');
      var noun = count === 1 ? 'device' : 'devices';
      return '<b>' + count + '</b> ' + noun +
        (scope ? ' <small>' + escapeHtml(scope) + '</small>' : '');
    },

    /* ---- rendering -------------------------------------------------- */

    renderMore: function () {
      if (this.rendered >= this.view.length) return;

      var frag = document.createDocumentFragment();
      var grouped = !this.query && this.cfg.groupResults;
      var end = Math.min(this.rendered + CHUNK, this.view.length);
      var lastGroup = this.lastGroupRendered;

      for (var i = this.rendered; i < end; i++) {
        var it = this.view[i];

        if (grouped && it.catLabel !== lastGroup) {
          var n = 0;
          for (var j = i; j < this.view.length && this.view[j].catLabel === it.catLabel; j++) n++;
          var head = document.createElement('h2');
          head.className = 'ds__group-head';
          head.innerHTML = escapeHtml(it.catLabel) + '<span class="ds__group-count">' + n + '</span>';
          frag.appendChild(head);
          lastGroup = it.catLabel;
          this.currentGrid = null;
        }

        if (!this.currentGrid || (grouped && this.currentGrid.getAttribute('data-group') !== lastGroup)) {
          this.currentGrid = document.createElement('ul');
          this.currentGrid.className = 'ds__grid';
          if (grouped) this.currentGrid.setAttribute('data-group', lastGroup);
          frag.appendChild(this.currentGrid);
        }

        this.currentGrid.appendChild(this.tile(it, i));
      }

      this.lastGroupRendered = lastGroup;
      this.rendered = end;
      this.results.appendChild(frag);
    },

    tile: function (it, i) {
      var li = document.createElement('li');
      li.className = 'ds__cell';
      li.style.setProperty('--i', String(Math.min(i, 20)));

      var lead = this.cfg.showThumbs && it.img
        ? '<span class="ds__thumb"><img src="' + escapeHtml(it.img) + '" alt="" loading="lazy" decoding="async" width="300" height="300"></span>'
        : glyph(it.cat);

      li.innerHTML =
        '<a class="ds__card" href="' + escapeHtml(it.url) + '" data-ds-handle="' + escapeHtml(it.url) + '">' +
        lead +
        '<span class="ds__body">' +
        '<span class="ds__name">' + this.mark(it.name) + (it.isNew ? '<span class="ds__new">New</span>' : '') + '</span>' +
        '<span class="ds__meta">' + escapeHtml(it.brand) + ' · ' + escapeHtml(it.catLabel) + '</span>' +
        '</span>' +
        '<span class="ds__chev" aria-hidden="true">›</span>' +
        '</a>';

      return li;
    },

    mark: function (name) {
      var q = this.query.trim();
      if (!q) return escapeHtml(name);
      var lower = name.toLowerCase();
      var at = lower.indexOf(q.toLowerCase());
      if (at === -1) return escapeHtml(name);
      return (
        escapeHtml(name.slice(0, at)) +
        '<mark>' + escapeHtml(name.slice(at, at + q.length)) + '</mark>' +
        escapeHtml(name.slice(at + q.length))
      );
    },

    /* ---- chips ------------------------------------------------------ */

    /* Facet counts are computed against every OTHER active facet, so a count
       is always "how many you get if you tick this". An option that would
       return nothing is disabled rather than left as a trap: picking Watches
       then Lenovo used to be reachable and returned an empty page. */
    matches: function (it, skip) {
      if (skip !== 'type' && this.types.length && this.types.indexOf(it.cat) === -1) return false;
      if (skip !== 'brand' && this.brands.length && this.brands.indexOf(it.brand) === -1) return false;
      if (skip !== 'series' && this.series.length && this.series.indexOf(it.series) === -1) return false;
      if (this.variants.length && this.variants.indexOf(it.variant) === -1) return false;
      return true;
    },


    /* On a pre-tagged arrival the brand is already decided, so the only axis
       that matters is the series — and a checkbox in a side rail is the wrong
       control for it. These are the pills from the Apple collection page:
       one row, counts, "All" to clear, one tap to narrow. */
    buildPills: function () {
      if (!this.pills) return;

      var self = this;
      var scoped = this.types.length > 0 || this.brands.length > 0;
      if (!scoped || this.query) {
        this.pills.hidden = true;
        this.root.classList.remove('has-pills');
        this.markRail();
        return;
      }

      var pool = this.index.filter(function (it) {
        if (self.types.length && self.types.indexOf(it.cat) === -1) return false;
        if (self.brands.length && self.brands.indexOf(it.brand) === -1) return false;
        return true;
      });

      if (pool.length < 2) {
        this.pills.hidden = true;
        this.root.classList.remove('has-pills');
        return;
      }

      function tally(key) {
        var counts = {};
        pool.forEach(function (it) {
          var v = it[key];
          if (v) counts[v] = (counts[v] || 0) + 1;
        });
        return counts;
      }

      var counts = tally('series');
      var names = Object.keys(counts);
      var top = 0;
      names.forEach(function (n) { if (counts[n] > top) top = counts[n]; });

      /* If one series covers nearly everything ("iPhone" on the Apple page) it
         is the set, not a filter — switch to the variant axis instead. */
      this.pillKey = 'series';
      if (names.length < 2 || top / pool.length > 0.8) {
        counts = tally('variant');
        names = Object.keys(counts);
        this.pillKey = 'variant';
      }

      names = names.filter(function (n) { return counts[n] > 0; }).sort(function (a, b) {
        return counts[b] - counts[a] || a.localeCompare(b);
      });

      if (names.length < 2) {
        this.pills.hidden = true;
        this.root.classList.remove('has-pills');
        this.markRail();
        return;
      }

      var active = this.pillKey === 'series' ? this.series : this.variants;
      var html = '<button type="button" class="ds__pill" data-ds-pill="" aria-pressed="' +
        (active.length ? 'false' : 'true') + '">All<i>' + pool.length + '</i></button>';

      names.forEach(function (n) {
        html += '<button type="button" class="ds__pill" data-ds-pill="' + escapeHtml(n) +
          '" aria-pressed="' + (active.indexOf(n) > -1 ? 'true' : 'false') + '">' +
          escapeHtml(n) + '<i>' + counts[n] + '</i></button>';
      });

      this.pills.innerHTML = html;
      this.pills.hidden = false;
      this.root.classList.add('has-pills');
      this.markRail();
    },

    /* A brand arriving from the menu is not a choice the visitor still needs,
       so its group goes; if that leaves nothing useful, the rail goes too. */
    markRail: function () {
      var brandPinned = this.brands.length === 1 && this.urlPinned;
      this.root.classList.toggle('is-brand-pinned', brandPinned);

      var groups = this.el.querySelectorAll('[data-ds-grp]');
      var visible = 0;
      for (var i = 0; i < groups.length; i++) {
        var g = groups[i];
        if (g.hidden) continue;
        if (brandPinned && g.hasAttribute('data-ds-brands')) continue;
        if (this.root.classList.contains('has-pills') && g.hasAttribute('data-ds-series')) continue;
        visible++;
      }
      this.root.classList.toggle('is-rail-empty', visible < 2 && brandPinned);
    },

    buildChips: function () {
      var self = this;
      var typeCounts = {};
      var brandCounts = {};
      var seriesCounts = {};
      var catBrand = {};
      var brandTotals = {};

      this.index.forEach(function (it) {
        if (self.matches(it, 'type')) typeCounts[it.cat] = (typeCounts[it.cat] || 0) + 1;
        if (self.matches(it, 'brand')) brandCounts[it.brand] = (brandCounts[it.brand] || 0) + 1;
        if (it.series && self.matches(it, 'series')) {
          seriesCounts[it.series] = (seriesCounts[it.series] || 0) + 1;
        }
        brandTotals[it.brand] = (brandTotals[it.brand] || 0) + 1;
        var key = it.cat + '|' + it.brand;
        catBrand[key] = (catBrand[key] || 0) + 1;
      });

      this.catBrandWeight = catBrand;

      /* ---- device type ---- */
      this.typeRow.innerHTML = '';
      var typeCap = this.cfg.typesVisible || 6;
      var shownTypes = 0;
      (this.cfg.categories || []).forEach(function (cat) {
        var n = typeCounts[cat.slug] || 0;
        var selected = self.types.indexOf(cat.slug) > -1;
        if (!n && !selected) return;
        var opt = self.chip('data-ds-type', cat.slug, cat.label, n);
        if (!n) opt.setAttribute('data-empty', 'true');
        if (shownTypes >= typeCap && !selected) opt.setAttribute('data-overflow', 'true');
        shownTypes++;
        self.typeRow.appendChild(opt);
      });

      if (this.typeMore) {
        var hiddenTypes = Math.max(0, shownTypes - typeCap);
        this.typeMoreLabel = 'Show ' + hiddenTypes + ' more';
        this.typeMore.hidden = hiddenTypes === 0;
        if (this.typeWrap.getAttribute('data-expanded') !== 'true') {
          this.typeMore.textContent = this.typeMoreLabel;
        }
      }

      /* ---- brand ---- */
      var brands = Object.keys(brandTotals).filter(function (b) {
        return brandCounts[b] > 0 || self.brands.indexOf(b) > -1;
      }).sort(function (a, b) {
        var selA = self.brands.indexOf(a) > -1 ? 1 : 0;
        var selB = self.brands.indexOf(b) > -1 ? 1 : 0;
        return selB - selA || (brandCounts[b] || 0) - (brandCounts[a] || 0) || a.localeCompare(b);
      });

      var visible = this.cfg.brandsVisible || 10;
      this.brandRow.innerHTML = '';
      brands.forEach(function (b, i) {
        var opt = self.chip('data-ds-brand', b, b, brandCounts[b] || 0);
        if (!brandCounts[b]) opt.setAttribute('data-empty', 'true');
        if (i >= visible) opt.setAttribute('data-overflow', 'true');
        self.brandRow.appendChild(opt);
      });

      var hidden = brands.length - visible;
      if (hidden > 0 && this.brandMore) {
        this.moreLabel = 'Show all ' + brands.length + ' brands';
        if (this.brandWrap.getAttribute('data-expanded') !== 'true') {
          this.brandMore.textContent = this.moreLabel;
        }
        this.brandMore.hidden = false;
      } else if (this.brandMore) {
        this.brandMore.hidden = true;
      }

      /* ---- series ---- */
      if (this.seriesRow && this.seriesWrap) {
        var brandPinned = this.brands.length > 0 || this.types.length > 0;
        var rail = this.seriesWrap.parentNode;
        var brandGrp = this.brandWrap;
        if (brandPinned && rail && brandGrp && this.seriesWrap.compareDocumentPosition(brandGrp) & Node.DOCUMENT_POSITION_PRECEDING) {
          rail.insertBefore(this.seriesWrap, brandGrp);
        }
      }
      if (this.seriesRow) {
        var seriesNames = Object.keys(seriesCounts)
          .filter(function (k) { return seriesCounts[k] > 1 || self.series.indexOf(k) > -1; })
          .sort(function (a, b) {
            var selA = self.series.indexOf(a) > -1 ? 1 : 0;
            var selB = self.series.indexOf(b) > -1 ? 1 : 0;
            return selB - selA || seriesCounts[b] - seriesCounts[a] || a.localeCompare(b);
          });

        this.seriesRow.innerHTML = '';
        var seriesCap = this.cfg.seriesVisible || 12;
        seriesNames.slice(0, seriesCap).forEach(function (name) {
          self.seriesRow.appendChild(
            self.chip('data-ds-series-value', name, name, seriesCounts[name])
          );
        });
        this.seriesWrap.hidden = seriesNames.length < 2;
      }

      /* ---- New badge ---- */
      var perCat = {};
      var newCap = this.cfg.newPerCategory || 3;
      var newDays = this.cfg.newDays || 240;
      var newCats = {};
      (this.cfg.categories || []).forEach(function (c) { if (c.markNew) newCats[c.slug] = true; });

      this.index.slice().sort(function (a, b) { return b.pub - a.pub; }).forEach(function (it) {
        it.isNew = false;
        // some categories were bulk-republished on one day, so their dates say
        // nothing about the device — those opt out of the badge entirely
        if (!it.pub || !newCats[it.cat]) return;
        perCat[it.cat] = (perCat[it.cat] || 0) + 1;
        if (perCat[it.cat] <= newCap && Date.now() - it.pub < newDays * 864e5) it.isNew = true;
      });
    },

    chip: function (attr, value, label, count) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'ds__opt';
      b.setAttribute(attr, value);
      b.setAttribute('aria-pressed', 'false');
      b.innerHTML =
        '<span class="ds__box" aria-hidden="true"></span>' +
        '<span class="ds__opt-label">' + escapeHtml(label) + '</span>' +
        '<span class="ds__opt-count">' + count + '</span>';
      return b;
    },

    toggleChip: function (chip) {
      if (chip.getAttribute('data-empty') === 'true') return;
      var kind = chip.hasAttribute('data-ds-type')
        ? 'type'
        : chip.hasAttribute('data-ds-brand') ? 'brand' : 'series';
      var attr = kind === 'type' ? 'data-ds-type' : kind === 'brand' ? 'data-ds-brand' : 'data-ds-series-value';
      var value = chip.getAttribute(attr);
      var list = kind === 'type' ? this.types : kind === 'brand' ? this.brands : this.series;
      var at = list.indexOf(value);
      if (at > -1) list.splice(at, 1);
      else list.push(value);
      this.apply();
      this.writeState();
      if (this.loaded) { this.buildChips(); }
      this.syncChips();
    },

    syncChips: function () {
      var self = this;
      if (this.filtersCount) {
        var active = this.types.length + this.brands.length + this.series.length;
        this.filtersCount.textContent = active;
        this.filtersCount.hidden = active === 0;
      }
      this.el.querySelectorAll('[data-ds-type]').forEach(function (c) {
        c.setAttribute('aria-pressed', self.types.indexOf(c.getAttribute('data-ds-type')) > -1 ? 'true' : 'false');
      });
      this.el.querySelectorAll('[data-ds-brand]').forEach(function (c) {
        c.setAttribute('aria-pressed', self.brands.indexOf(c.getAttribute('data-ds-brand')) > -1 ? 'true' : 'false');
      });
      this.el.querySelectorAll('[data-ds-series-value]').forEach(function (c) {
        c.setAttribute('aria-pressed', self.series.indexOf(c.getAttribute('data-ds-series-value')) > -1 ? 'true' : 'false');
      });
    },

    /* ---- keyboard grid ---------------------------------------------- */

    columns: function () {
      var grid = this.el.querySelector('.ds__grid');
      if (!grid) return 1;
      return Math.max(1, getComputedStyle(grid).gridTemplateColumns.split(' ').length);
    },

    move: function (delta, fromInput) {
      var cells = this.el.querySelectorAll('.ds__cell');
      if (!cells.length) return;
      var next = fromInput ? 0 : this.activeIdx + delta;
      if (next < 0) next = 0;
      if (next >= cells.length) {
        if (this.rendered < this.view.length) {
          this.renderMore();
          cells = this.el.querySelectorAll('.ds__cell');
        }
        next = Math.min(next, cells.length - 1);
      }
      this.activeIdx = next;
      this.paintActive();
      var link = cells[next].querySelector('.ds__tile');
      if (link) link.focus({ preventScroll: false });
    },

    paintActive: function () {
      var self = this;
      this.el.querySelectorAll('.ds__cell.is-active').forEach(function (c) {
        c.classList.remove('is-active');
      });
      var cells = this.el.querySelectorAll('.ds__cell');
      if (self.activeIdx > -1 && cells[self.activeIdx]) cells[self.activeIdx].classList.add('is-active');
    },

    /* ---- states ------------------------------------------------------ */

    showEmpty: function (q, qs) {
      var self = this;
      this.empty.hidden = false;

      var titleEl = this.empty.querySelector('[data-ds-empty-title]');
      var textEl = this.empty.querySelector('[data-ds-empty-text]');
      var clearBtn = this.empty.querySelector('[data-ds-empty-clear]');

      this.suggests.innerHTML = '';

      if (!this.query) {
        // filters only — no query to misspell, so do not quote an empty string
        var picked = [].concat(this.types.map(function (t) {
          var c = (self.cfg.categories || []).filter(function (x) { return x.slug === t; })[0];
          return c ? c.label : t;
        }), this.brands, this.series);

        if (titleEl) titleEl.textContent = 'Nothing matches that combination';
        if (textEl) {
          textEl.textContent = picked.length
            ? 'We do not cut ' + picked.join(' + ') + '. Drop one of the filters to widen it.'
            : 'Drop a filter to widen the search.';
        }
        if (clearBtn) clearBtn.hidden = false;
        return;
      }

      if (titleEl) titleEl.innerHTML = 'No device matches “<span data-ds-empty-query></span>”';
      var qEl = this.empty.querySelector('[data-ds-empty-query]');
      if (qEl) qEl.textContent = this.query;
      if (textEl) textEl.textContent = this.cfg.emptyText || '';
      if (clearBtn) clearBtn.hidden = !(this.types.length || this.brands.length || this.series.length);

      // loosest possible pass, ignoring facets, so we can still offer a route
      var near = [];
      for (var i = 0; i < this.index.length; i++) {
        var it = this.index[i];
        var d = editDistance(qs.slice(0, 14), it.s.slice(0, 14), 3);
        if (d <= 3) near.push({ it: it, d: d });
      }
      near.sort(function (a, b) { return a.d - b.d; });

      near.slice(0, 5).forEach(function (n) {
        var li = document.createElement('li');
        li.innerHTML = '<a href="' + escapeHtml(n.it.url) + '" data-ds-handle="' + escapeHtml(n.it.url) + '">' + escapeHtml(n.it.name) + '</a>';
        self.suggests.appendChild(li);
      });
    },

    showError: function () {
      var self = this;
      this.errorBox.hidden = false;
      var retry = this.errorBox.querySelector('button');
      if (retry) {
        retry.addEventListener('click', function () {
          self.errorBox.hidden = true;
          self.skeleton.hidden = false;
          self.seen = {};
          self.seenName = {};
          self.index = [];
          self.load();
        }, { once: true });
      }
    },

    /* ---- recent ------------------------------------------------------ */

    remember: function (url) {
      try {
        var list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
        list = list.filter(function (u) { return u !== url; });
        list.unshift(url);
        localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, 8)));
      } catch (e) { /* storage off */ }
    },

    paintRecent: function () {
      if (!this.recent) return;
      var urls = [];
      try {
        urls = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      } catch (e) { urls = []; }
      if (!urls.length) return;

      var byUrl = {};
      this.index.forEach(function (it) { byUrl[it.url] = it; });

      var html = '';
      var n = 0;
      urls.forEach(function (u) {
        var it = byUrl[u];
        if (!it) return;
        n++;
        html +=
          '<li><a class="ds__recent-link" href="' + escapeHtml(it.url) + '">' +
          glyph(it.cat) + escapeHtml(it.name) + '</a></li>';
      });

      this.recentCount = n;
      if (!n) return;
      this.recentRow.innerHTML = html;
      this.recent.hidden = !!(this.query || this.types.length || this.brands.length);
    },

    /* ---- url state ---------------------------------------------------- */

    readState: function () {
      var p = new URLSearchParams(window.location.search);
      this.query = p.get('q') || '';
      this.types = (p.get('type') || '').split(',').filter(Boolean);
      this.brands = (p.get('brand') || '').split(',').filter(Boolean);
      this.series = (p.get('series') || '').split(',').filter(Boolean);
      this.variants = (p.get('variant') || '').split(',').filter(Boolean);
      this.sort = p.get('sort') === 'az' ? 'az' : 'new';
      if (this.input) this.input.value = this.query;
    },

    writeState: function () {
      var p = new URLSearchParams(window.location.search);
      this.query ? p.set('q', this.query) : p.delete('q');
      this.types.length ? p.set('type', this.types.join(',')) : p.delete('type');
      this.brands.length ? p.set('brand', this.brands.join(',')) : p.delete('brand');
      this.series.length ? p.set('series', this.series.join(',')) : p.delete('series');
      this.variants.length ? p.set('variant', this.variants.join(',')) : p.delete('variant');
      this.sort === 'az' ? p.set('sort', 'az') : p.delete('sort');
      var qs = p.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? '?' + qs : ''));
    },

    setQuery: function (v) {
      this.query = v;
      this.input.value = v;
      this.apply();
      this.writeState();
    }
  };

  /* --------------------------------------------------------- custom el */

  if (!customElements.get('device-search')) {
    customElements.define(
      'device-search',
      class extends HTMLElement {
        connectedCallback() {
          if (this._booted) return;
          this._booted = true;
          this.controller = new DeviceSearch();
          this.controller.el = this;
          this.controller.seen = {};
          this.controller.seenName = {};
          this.controller.boot();
        }

        disconnectedCallback() {
          if (this.controller && this.controller.io) this.controller.io.disconnect();
        }
      }
    );
  }

})();
